import { lazy, Suspense, useEffect } from 'react'
import ChunkErrorBoundary from './components/ChunkErrorBoundary'
import SessionLostBanner from './components/SessionLostBanner'
import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import { useTheme } from './hooks/useTheme'
import { useAuth } from './contexts/AuthContext'
import { useNavigationLogger } from './hooks/useNavigationLogger'
import { useClickLogger } from './hooks/useClickLogger'
import { logger } from './utils/logger'
import Layout from './components/LayoutComponent'
import ProtectedRoute from './components/ProtectedRoute'
import UniversalAssetRedirect from './components/UniversalAssetRedirect'
import ScrollToTopButton from './components/ScrollToTopButton'

const HomePage = lazy(() => import('./pages/HomePage'))
const ScanPage = lazy(() => import('./pages/ScanPage'))
const AssetPage = lazy(() => import('./pages/AssetPage'))
const LoginPage = lazy(() => import('./pages/LoginPage'))
const ContractSelectPage = lazy(() => import('./pages/ContractSelectPage'))
const ContractPage = lazy(() => import('./pages/ContractPage'))
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'))
const DocumentPage = lazy(() => import('./pages/DocumentPage'))
const WaybillPage = lazy(() => import('./pages/WaybillPage'))
const LabTransferPage = lazy(() => import('./pages/LabTransferPage'))
const LabTransfersListPage = lazy(() => import('./pages/LabTransfersListPage'))
// Листы контрактов живут внутри Журнала СИ (режим «Листы (МО)»); старые адреса /sheets ведут туда
function SheetRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={`/journal?mode=sheets&sheet=${id}`} replace />
}
const TagsPage = lazy(() => import('./pages/TagsPage'))
const CustomerPage = lazy(() => import('./pages/CustomerPage'))
const CustomerContractPage = lazy(() => import('./pages/CustomerContractPage'))
const CustomerAssetPage = lazy(() => import('./pages/CustomerAssetPage'))
const DeadlinesPage = lazy(() => import('./pages/DeadlinesPage'))
const JournalPage = lazy(() => import('./pages/JournalPage'))
const PrintTagPage = lazy(() => import('./pages/PrintTagPage'))
const ShelvesPage = lazy(() => import('./pages/ShelvesPage'))
const ChatMovedPage = lazy(() => import('./pages/ChatMovedPage'))
const DistributionsPage = lazy(() => import('./pages/DistributionsPage'))
const DistributionTabPage = lazy(() => import('./pages/DistributionTabPage'))
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'))
const AcceptancePhotoMatchPage = lazy(() => import('./pages/AcceptancePhotoMatchPage'))
const PublicRequestPage = lazy(() => import('./pages/PublicRequestPage'))
const PublicRequestStatusPage = lazy(() => import('./pages/PublicRequestStatusPage'))
const ChangelogPage = lazy(() => import('./pages/ChangelogPage'))
const SubcontractorApplicationPage = lazy(() => import('./pages/SubcontractorApplicationPage'))
const EmployeeAccountPage = lazy(() => import('./pages/EmployeeAccountPage'))

function RouteLoadingFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" />
    </div>
  )
}

function AppContent() {
  const { theme, toggleTheme } = useTheme()
  const { isAuthenticated, isLoading } = useAuth()
  
  // Инициализируем логирование
  useNavigationLogger()
  useClickLogger()

  useEffect(() => {
    logger.info('APP', 'Инициализация приложения')
  }, [])

  useEffect(() => {
    // Применяем тему к body
    logger.debug('APP', 'Применение темы', { theme })
    document.documentElement.classList.toggle('dark', theme === 'dark')
  }, [theme])

  // Логируем изменения авторизации (только в development)
  useEffect(() => {
    if (import.meta.env.MODE === 'development') {
      logger.info('APP', 'Состояние авторизации изменилось', { 
        isAuthenticated, 
        isLoading,
        timestamp: new Date().toISOString()
      })
    }
  }, [isAuthenticated, isLoading])

  const assetRouteElement = isLoading ? (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
    </div>
  ) : isAuthenticated ? (
    <Layout theme={theme} onThemeToggle={toggleTheme}>
      <AssetPage />
    </Layout>
  ) : (
    <CustomerAssetPage />
  )

  return (
    <>
      <SessionLostBanner />
      <ChunkErrorBoundary>
      <Suspense fallback={<RouteLoadingFallback />}>
      <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/customer" element={<CustomerPage />} />
      <Route path="/customer/contract/:code" element={<CustomerContractPage />} />
      <Route path="/customer/asset/:uid" element={<CustomerAssetPage />} />
      <Route path="/customer/u/:uid" element={<CustomerAssetPage />} />
      <Route path="/request" element={<PublicRequestPage theme={theme} onThemeToggle={toggleTheme} />} />
      <Route path="/request/status" element={<PublicRequestStatusPage theme={theme} onThemeToggle={toggleTheme} />} />
      <Route path="/u/:uid" element={<UniversalAssetRedirect />} />
      <Route path="/" element={
        <ProtectedRoute>
          <Layout theme={theme} onThemeToggle={toggleTheme}>
            <HomePage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/contract-select" element={
        <ProtectedRoute>
          <Layout theme={theme} onThemeToggle={toggleTheme}>
            <ContractSelectPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/contracts/:code" element={
        <ProtectedRoute>
          <Layout theme={theme} onThemeToggle={toggleTheme}>
            <ContractPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/contracts/:code/photos" element={
        <ProtectedRoute>
          <Layout theme={theme} onThemeToggle={toggleTheme}>
            <AcceptancePhotoMatchPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/scan" element={
        <ProtectedRoute>
          <Layout theme={theme} onThemeToggle={toggleTheme}>
            <ScanPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/asset/:uid" element={assetRouteElement} />
      <Route path="/docs" element={
        <ProtectedRoute>
          <Layout theme={theme} onThemeToggle={toggleTheme}>
            <DocumentsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/docs/:id" element={
        <ProtectedRoute>
          <Layout theme={theme} onThemeToggle={toggleTheme}>
            <DocumentPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/subcontractor-applications/:id" element={
        <ProtectedRoute>
          <Layout theme={theme} onThemeToggle={toggleTheme}>
            <SubcontractorApplicationPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/print-tag/:id" element={
        <ProtectedRoute>
          <Layout theme={theme} onThemeToggle={toggleTheme}>
            <PrintTagPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/sheets" element={<Navigate to="/journal?mode=sheets" replace />} />
      <Route path="/sheets/:id" element={<SheetRedirect />} />
      <Route path="/lab-transfers" element={
        <ProtectedRoute>
          <Layout theme={theme} onThemeToggle={toggleTheme}>
            <LabTransfersListPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/lab-transfers/:id" element={
        <ProtectedRoute>
          <Layout theme={theme} onThemeToggle={toggleTheme}>
            <LabTransferPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/waybill/:id" element={
        <ProtectedRoute>
          <Layout theme={theme} onThemeToggle={toggleTheme}>
            <WaybillPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/tags" element={
        <ProtectedRoute>
          <Layout theme={theme} onThemeToggle={toggleTheme}>
            <TagsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/deadlines" element={
        <ProtectedRoute>
          <Layout theme={theme} onThemeToggle={toggleTheme}>
            <DeadlinesPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/journal" element={
        <ProtectedRoute>
          <Layout theme={theme} onThemeToggle={toggleTheme}>
            <JournalPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/shelves" element={
        <ProtectedRoute>
          <Layout theme={theme} onThemeToggle={toggleTheme}>
            <ShelvesPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/chat" element={<ChatMovedPage />} />
      <Route path="/distributions" element={
        <ProtectedRoute>
          <Layout theme={theme} onThemeToggle={toggleTheme}>
            <DistributionsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/distributions/:id" element={
        <ProtectedRoute>
          <Layout theme={theme} onThemeToggle={toggleTheme}>
            <DistributionTabPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute>
          <Layout theme={theme} onThemeToggle={toggleTheme}>
            <NotificationsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/changelog" element={
        <ProtectedRoute>
          <Layout theme={theme} onThemeToggle={toggleTheme}>
            <ChangelogPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Layout theme={theme} onThemeToggle={toggleTheme}>
            <EmployeeAccountPage />
          </Layout>
        </ProtectedRoute>
      } />
      </Routes>
      </Suspense>
      </ChunkErrorBoundary>
      <ScrollToTopButton />
    </>
  )
}

export default function App() {
  return <AppContent />
}
